import { useMemo } from 'react'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import { digObject, sortArrayBy } from '@domain-group/javascript-utils'

import useSelector from '@hooks/useSelector'

import type { TemplatePageModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['savedArtworkPages', 'serviceProducts', 'services', 'templatePages']

export type ServiceGroup = {
  groupKey: string,
  products: { id: number, title: string }[] | [],
}

type GroupedServiceProducts = {
  [key: string]: ServiceGroup,
}

type GroupServiceProductsParams = {
  serviceProducts: EntitiesState['serviceProducts'],
  services: EntitiesState['services'],
  templatePage: TemplatePageModel,
}

const groupServiceProducts = (params: GroupServiceProductsParams) => {
  const { serviceProducts, services, templatePage } = params

  const permissionProductIds: number[] = digObject(templatePage, 'permission_ids.products', [])

  const grouped: GroupedServiceProducts = {}

  permissionProductIds.forEach((serviceProductId) => {
    const serviceProduct = serviceProducts[serviceProductId] || {}
    const { service_id } = serviceProduct

    const serviceTitle = services[service_id]?.title || 'No Service'

    if (!grouped[service_id]){
      grouped[service_id] = {
        groupKey: serviceTitle,
        products: [],
      }
    }

    // @ts-expect-error TS2345
    grouped[service_id].products.push(serviceProduct)
  })

  // sort by groupKey
  const groupedArray = Object.values(grouped)
  return sortArrayBy(groupedArray, 'asc', 'groupKey')
}

type TemplatePageDetailsParams = {
  templatePage: TemplatePageModel,
}

const useTemplatePageDetails = (params: TemplatePageDetailsParams) => {
  const { templatePage } = params

  const {
    updatedEntities: {
      serviceProducts: serviceProductsUpdatedAt,
      services: servicesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { serviceProducts, services } = useSelector(reduxState => reduxState.entities)

  const groupedProductsByService = useMemo(
    () => groupServiceProducts({ serviceProducts, services, templatePage }),
    [
      templatePage.id,
      servicesUpdatedAt,
      serviceProductsUpdatedAt,
    ],
  )

  return {
    groupedProductsByService,
  }
}

export default useTemplatePageDetails
